/**
 * ListenTo — "Listen ›" button + inline audio player for a ChatsPulse
 * article. Lazily generates an OpenAI TTS MP3 (cached in object
 * storage) on first click, then swaps in a native <audio> player.
 *
 * Optional `lang` prop plays the pre-translated version.
 */
import { useCallback, useEffect, useState } from "react";
import { Headphones, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const VOICES = [
  { code: "alloy",  label: "Alloy · Neutral" },
  { code: "nova",   label: "Nova · Energetic" },
  { code: "shimmer",label: "Shimmer · Bright" },
  { code: "onyx",   label: "Onyx · Deep" },
  { code: "echo",   label: "Echo · Smooth" },
  { code: "fable",  label: "Fable · Storyteller" },
];

export default function ListenTo({ articleId, lang = null, testIdPrefix = "listen" }) {
  const [voice, setVoice] = useState("alloy");
  const [state, setState] = useState({ status: "unknown", meta: null, error: null });

  // Probe existing cache on mount / voice / lang change
  useEffect(() => {
    if (!articleId) return;
    let alive = true;
    (async () => {
      try {
        const q = new URLSearchParams({ voice });
        const r = await fetch(`${API}/articles/${articleId}/audio?${q}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!alive) return;
        setState(d.ready
          ? { status: "ready", meta: d, error: null }
          : { status: "idle", meta: null, error: null });
      } catch (_e) {
        if (alive) setState({ status: "idle", meta: null, error: null });
      }
    })();
    return () => { alive = false; };
  }, [articleId, voice, lang]);

  const generate = useCallback(async () => {
    setState({ status: "loading", meta: null, error: null });
    try {
      const q = new URLSearchParams({ voice });
      if (lang) q.set("lang", lang);
      const r = await fetch(`${API}/articles/${articleId}/audio?${q}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setState({ status: "ready", meta: d, error: null });
    } catch (e) {
      setState({ status: "error", meta: null, error: e.message });
    }
  }, [articleId, voice, lang]);

  const audioUrl = state.meta?.audio_url
    ? (state.meta.audio_url.startsWith("http")
        ? state.meta.audio_url
        : `${process.env.REACT_APP_BACKEND_URL || ""}${state.meta.audio_url}`)
    : null;

  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white shadow-sm px-2 py-1 max-w-full"
      data-testid={testIdPrefix}
    >
      <Headphones className="w-4 h-4 text-blue-600 shrink-0" />
      <span className="text-xs font-semibold text-slate-700 mr-1">Listen To</span>
      <select
        value={voice}
        onChange={(e) => setVoice(e.target.value)}
        className="text-xs bg-transparent border-none outline-none py-0 pr-1 max-w-[110px]"
        data-testid={`${testIdPrefix}-voice`}
        title="Pick a voice"
      >
        {VOICES.map((v) => (
          <option key={v.code} value={v.code}>{v.label}</option>
        ))}
      </select>

      {state.status === "ready" && audioUrl ? (
        <audio
          controls
          src={audioUrl}
          className="h-8 max-w-[240px]"
          data-testid={`${testIdPrefix}-player`}
        />
      ) : state.status === "loading" ? (
        <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`${testIdPrefix}-loading`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
        </span>
      ) : state.status === "error" ? (
        <span className="inline-flex items-center gap-1 text-xs text-red-600" title={state.error} data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="w-3.5 h-3.5" /> Try again
        </span>
      ) : (
        <button
          type="button"
          onClick={generate}
          className="text-xs font-semibold text-blue-700 hover:text-blue-900 px-2 py-0.5 rounded-full hover:bg-blue-50"
          data-testid={`${testIdPrefix}-play`}
        >
          Play ›
        </button>
      )}
    </div>
  );
}
